import { useState } from "react";
import { pricing } from "../lib/content";
import { CheckIcon } from "./Icons";
import SmartLink from "./SmartLink";

/**
 * Prices come from qost.ai/pricing.
 *
 * The annual figure is DERIVED from the "Save 20%" claim on that page rather
 * than being invented - see the note on `pricing` in content.ts. If your billing
 * system quotes a different annual rate, set it explicitly there.
 */
export default function Pricing({ withHeader = true }: { withHeader?: boolean }) {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="section-pad bg-white">
      <div className="container-page">
        {withHeader && (
          <div className="max-w-2xl">
            <span className="eyebrow">{pricing.eyebrow}</span>
            <h2 className="h-section mt-4">{pricing.heading}</h2>
            <p className="lead">{pricing.subheading}</p>
          </div>
        )}

        {/* Billing period toggle */}
        <div
          className={`inline-flex items-center gap-1 rounded-xl border border-ink-line bg-ink-soft p-1 ${
            withHeader ? "mt-8" : ""
          }`}
        >
          {(["Monthly", "Annual"] as const).map((label) => {
            const isAnnual = label === "Annual";
            const active = isAnnual === annual;
            return (
              <button
                key={label}
                type="button"
                aria-pressed={active}
                onClick={() => setAnnual(isAnnual)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                  active ? "bg-white text-ink shadow-card" : "text-ink-muted hover:text-ink"
                }`}
              >
                {label}
                {isAnnual && (
                  <span className="ml-2 rounded-md bg-accent-success/15 px-2 py-0.5 text-[11px] font-semibold text-accent-success">
                    {pricing.annualNote}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          {pricing.tiers.map((tier) => {
            const derived =
              tier.monthly !== null
                ? annual
                  ? tier.monthly * (1 - pricing.annualDiscount)
                  : tier.monthly
                : null;

            return (
              <div
                key={tier.name}
                className={`card relative flex flex-col p-7 ${
                  tier.highlighted
                    ? "border-brand-300 shadow-lift ring-1 ring-brand-200"
                    : "shadow-card"
                }`}
              >
                {tier.highlighted && (
                  <span className="absolute -top-3 left-7 rounded-full bg-brand-500 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                    Most popular
                  </span>
                )}

                <h3 className="text-lg font-semibold text-ink">{tier.name}</h3>
                <p className="mt-2 min-h-[2.75rem] text-sm leading-relaxed text-ink-muted">
                  {tier.blurb}
                </p>

                <div className="mt-6 flex items-baseline gap-1.5">
                  <span className="text-4xl font-extrabold tracking-tight text-ink">
                    {derived !== null ? `$${derived.toFixed(0)}` : tier.price}
                  </span>
                  {derived !== null && (
                    <span className="text-sm font-medium text-ink-muted">/month</span>
                  )}
                </div>
                {derived !== null && annual && (
                  <p className="mt-1 text-xs font-medium text-accent-success">
                    billed annually
                  </p>
                )}

                <SmartLink
                  href={tier.cta.href}
                  className={`mt-7 w-full ${tier.highlighted ? "btn-primary" : "btn-ghost"}`}
                >
                  {tier.cta.label}
                </SmartLink>

                <ul className="mt-7 space-y-3 border-t border-ink-line pt-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm text-ink">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                        <CheckIcon className="h-3 w-3" />
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col items-center gap-4">
          <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {pricing.footnotes.map((note) => (
              <li key={note} className="text-xs font-medium text-ink-muted">
                {note}
              </li>
            ))}
          </ul>
          <p className="text-sm text-ink-muted">
            Read the full{" "}
            <a href="/pricing" className="font-semibold text-brand-600 hover:text-brand-700">
              pricing details
            </a>{" "}
            or{" "}
            <a href="/#contact" className="font-semibold text-brand-600 hover:text-brand-700">
              talk to sales
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
}
